import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiError } from "../lib/api";
import { canManageMembers, useWorkspace } from "../features/workspace/queries";
import { useI18n } from "../i18n/I18nProvider";
import { messageKeyForError } from "../i18n/errors";
import { forgetWorkspace, rememberWorkspace } from "../lib/recent-workspaces";
import { TabNav, type TabNavItem } from "../components/ui/TabNav";
import { Button } from "../components/ui/Button";
import { ACTION_LINK_CLASS } from "../components/ui/action-link";
import { ErrorState } from "../components/ui/ErrorState";
import type { MessageKey } from "../i18n/en";

export interface WorkspaceLayoutProps {
  workspaceId: string;
}

const TABS = [
  { to: "/app/$workspaceId/roster", labelKey: "nav.roster" },
  { to: "/app/$workspaceId/configurations", labelKey: "nav.configurations" },
  { to: "/app/$workspaceId/desktops", labelKey: "nav.desktops" },
  { to: "/app/$workspaceId/members", labelKey: "nav.members" },
  { to: "/app/$workspaceId/usage", labelKey: "nav.usage" },
] as const satisfies readonly { to: string; labelKey: MessageKey }[];

type TabTo = (typeof TABS)[number]["to"];

/** A workspace that cannot be opened: the problem in plain words, and a way back to the list. */
function WorkspaceUnavailable({
  workspaceId,
  error,
  onRetry,
}: {
  workspaceId: string;
  error: unknown;
  onRetry: () => void;
}) {
  const { t } = useI18n();
  const missing = error instanceof ApiError && (error.status === 403 || error.status === 404);
  return (
    <ErrorState
      action={
        missing ? (
          <Link
            to="/app"
            onClick={() => forgetWorkspace(workspaceId)}
            className={ACTION_LINK_CLASS}
          >
            {t("workspace.backToList")}
          </Link>
        ) : (
          <Button onClick={onRetry}>{t("app.retry")}</Button>
        )
      }
    >
      {missing ? t("workspace.missing") : t(messageKeyForError(error))}
    </ErrorState>
  );
}

/** Workspace shell: loads the workspace and shows the section tabs under the shell band. */
export function WorkspaceLayout({ workspaceId }: WorkspaceLayoutProps) {
  const { t } = useI18n();
  const workspace = useWorkspace(workspaceId);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const data = workspace.data;

  useEffect(() => {
    if (data) rememberWorkspace(workspaceId);
  }, [data, workspaceId]);

  if (workspace.isError) {
    return (
      <WorkspaceUnavailable
        workspaceId={workspaceId}
        error={workspace.error}
        onRetry={() => void workspace.refetch()}
      />
    );
  }
  if (!data) {
    return <p className="text-operate text-ink-2">{t("app.loading")}</p>;
  }

  const showMembers = canManageMembers(data.role);
  const tabs = TABS.filter((tab) => tab.labelKey !== "nav.members" || showMembers);
  const items: TabNavItem[] = tabs.map((tab) => {
    const base = tab.to.replace("$workspaceId", workspaceId);
    return {
      key: tab.to,
      label: t(tab.labelKey),
      href: base,
      active: pathname === base || pathname.startsWith(`${base}/`),
    };
  });
  const tabByKey = new Map<string, TabTo>(tabs.map((tab) => [tab.to, tab.to]));

  return (
    <div className="space-y-6">
      <TabNav
        ariaLabel={t("nav.label")}
        items={items}
        renderLink={(item, className) => {
          const to = tabByKey.get(item.key);
          return to ? (
            <Link to={to} params={{ workspaceId }} className={className}>
              {item.label}
            </Link>
          ) : null;
        }}
      />
      <Outlet />
    </div>
  );
}
